import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000Panel from '../../../u2000Core/components/layout/U2000Panel';
import U2000YesNoDialog from '../../../u2000Core/components/dialog/U2000YesNoDialog';
import U2000InfoDialog from '../../../u2000Core/components/dialog/U2000InfoDialog';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { NS } from '../../U3147';
import { useU3147Context } from '../../U3147Context';
import { useU3147DayHandlers } from '../../U3147UseDayHandlers';
import { useU3147ExportHandlers } from '../../U3147UseExportHandlers';
import { u3147IsSessionRunning, type U3147TimeSession } from '../../U3147Service';
import U3147TimerCard from './U3147TimerCard';
import U3147DayBar from './U3147DayBar';
import U3147DayTimeline from './U3147DayTimeline';

const U3147TodayView: React.FC = () => {
  const { t } = useTranslation();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { selectedDate, setSelectedDate, viewEmployeeId, refreshDayKey, bumpRefreshDay } = useU3147Context();
  const { loading, dayResult, loadDay } = useU3147DayHandlers();
  const exportHandlers = useU3147ExportHandlers();
  const [optimisticRunningSession, setOptimisticRunningSession] = useState<U3147TimeSession | null>(null);

  const refreshDay = useCallback(
    (dateOverride?: string) => {
      const date = (dateOverride ?? selectedDate).slice(0, 10);
      void loadDay(date, viewEmployeeId ?? undefined);
      bumpRefreshDay();
    },
    [loadDay, selectedDate, viewEmployeeId, bumpRefreshDay],
  );

  useEffect(() => {
    setLogicalUnitBreadcrumb([{ label: t('title', { ns: NS }) }, { label: t('tabToday', { ns: NS }) }]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [t, setLogicalUnitBreadcrumb]);

  useEffect(() => {
    void loadDay(selectedDate, viewEmployeeId ?? undefined);
  }, [selectedDate, viewEmployeeId, refreshDayKey, loadDay]);

  const sessions = dayResult?.sessions ?? [];
  const runningSession = useMemo(() => {
    const fromDay = sessions.find((s) => u3147IsSessionRunning(s)) ?? null;
    if (fromDay) return fromDay;
    if (optimisticRunningSession && u3147IsSessionRunning(optimisticRunningSession)) {
      return optimisticRunningSession;
    }
    return null;
  }, [sessions, optimisticRunningSession]);

  useEffect(() => {
    if (sessions.some((s) => u3147IsSessionRunning(s))) {
      setOptimisticRunningSession(null);
    }
  }, [sessions]);

  const handleTimerStarted = useCallback(
    (session: U3147TimeSession) => {
      setOptimisticRunningSession(session);
      const sessionDate = session.workDate?.slice(0, 10);
      if (sessionDate && sessionDate !== selectedDate.slice(0, 10)) {
        setSelectedDate(sessionDate);
      }
    },
    [selectedDate, setSelectedDate],
  );

  const handleTimerStopped = useCallback(() => {
    setOptimisticRunningSession(null);
  }, []);
  const totalHours = dayResult?.totalHours ?? 0;

  return (
    <U2000Panel className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
      <div className="flex flex-col">
        <U3147TimerCard
          runningSession={runningSession}
          daySessions={sessions}
          onTimerChanged={refreshDay}
          onTimerStarted={handleTimerStarted}
          onTimerStopped={handleTimerStopped}
        />

        <U3147DayBar
          selectedDate={selectedDate}
          totalHours={totalHours}
          onDateChange={setSelectedDate}
        />

        <U3147DayTimeline
          sessions={sessions}
          loading={loading}
          selectedDate={selectedDate}
          totalMinutesFromApi={dayResult?.totalMinutes}
          onRefresh={refreshDay}
          onExportDay={() => exportHandlers.requestExport({ date: selectedDate.slice(0, 10) })}
          exportBusy={exportHandlers.busy}
        />
      </div>

      <U2000YesNoDialog
        isOpen={exportHandlers.confirmOpen}
        onClose={(result) => {
          if (result === 'yes') {
            void exportHandlers.executeExport().then((res) => {
              if (res) refreshDay();
            });
          } else {
            exportHandlers.cancelExport();
          }
        }}
        title={t('lblExportConfirmTitle', { ns: NS, defaultValue: 'Exporter la journée' })}
      >
        {t('msgExportConfirm', {
          ns: NS,
          date: selectedDate.slice(0, 10),
          defaultValue: "Confirmer l'export des plages clôturées du {{date}} vers votre feuille de temps ?",
        })}
      </U2000YesNoDialog>

      <U2000InfoDialog
        isOpen={!!exportHandlers.warnings?.length}
        onClose={() => {
          exportHandlers.clearWarnings();
          refreshDay();
        }}
        title={t('lblExportWarnings', { ns: NS, defaultValue: 'Avertissements export' })}
      >
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {(exportHandlers.warnings ?? []).map((w, i) => (
            <li key={`${i}-${w}`}>{w}</li>
          ))}
        </ul>
        {exportHandlers.lastTimesheetId && (
          <button
            type="button"
            className="mt-4 text-sm font-medium text-blue-600 hover:underline"
            onClick={() => exportHandlers.openTimesheetInNewTab(exportHandlers.lastTimesheetId!)}
          >
            {t('btnViewTimesheet', { ns: NS, defaultValue: 'Voir ma feuille de temps' })}
          </button>
        )}
      </U2000InfoDialog>
    </U2000Panel>
  );
};

export default U3147TodayView;
